import { Form } from 'ca-api-client';

import options from '@/components/form/custom/options';
import { getters, mutations, actions } from './common.js';

const defaultState = () => {
    return {
        id: null,
        name: null,
        form: {
            display: 'form',
            components: [],
            meta: {
                assigns_users: false,
                map_comments: false,
                reactions_enabled: false,
                sync_enabled: false,
                uses_replies: false,
                uses_multiple_submissions: false,
                uses_approval: false,
                include_engagement: false,
                is_dataroom_form: false,
                uses_issues: false,
            },
        },
        messages: {
            submission_success_message: null,
            submission_error_message: null,
        },
        emailReceipts: {
            send_admin_email: false,
            admin_recipients: [],
            include_admin_summary: true,
            admin_email_message: null,
            send_stakeholder_email: false,
            stakeholder_reply_to: [],
            include_stakeholder_summary: false,
            stakeholder_email_message: null,
            stakeholder_email_footer: null,
            stakeholder_email_subject: null,
            hide_stakeholder_email_header: false,
            hide_admin_email_header: false,
        },
        issues: {
            categories: {
                sync: []
            },
        },
    };
};

export const forms = {
    /**
     * global forms
     */
    namespaced: true,
    state: {
        options,
        validator: null,
        tinymceConfig: {
            branding: false,
            height: 350,
            menubar: false,
            toolbar: 'paragraph bold italic underline link image',
            api_key: ca.tinymce.api_key,
            convert_urls: false,
            plugins: [
                'image',
                'autolink lists link anchor',
                'paste code help wordcount',
            ],
        },
    },
    getters: {
        validator(state) {
            return state.validator;
        },
    },
    mutations: {
        validator(state, validator) {
            state.validator = validator;
        },
    },
    actions: {
        setValidator({ commit }, validator) {
            commit('validator', validator);
        },
    },
    /**
     * nested modules
     */
    modules: {
        /**
         * forms/create
         */
        create: {
            namespaced: true,
            state: {
                ...defaultState(),
            },
            getters,
            mutations,
            actions: {
                ...actions,
                async createForm({ state, commit }) {
                    const form = new Form({
                        name: state.name,
                        config: state.form,
                        client_id: ca.client.uuid,
                        project_id: ca.project.uuid,
                        ...state.messages,
                        ...state.emailReceipts,
                        ...state.issues,
                    });

                    const newForm = await form.save();

                    const {
                        id,
                        name,
                        config,
                        submission_success_message,
                        submission_error_message,
                        send_admin_email,
                        admin_recipients,
                        include_admin_summary,
                        admin_email_message,
                        send_stakeholder_email,
                        stakeholder_reply_to,
                        include_stakeholder_summary,
                        stakeholder_email_message,
                        stakeholder_email_footer,
                        stakeholder_email_subject,
                        hide_stakeholder_email_header,
                        hide_admin_email_header,
                        categories,
                    } = newForm;

                    commit('id', id);
                    commit('name', name);
                    commit('form', config);
                    commit('messages', {
                        submission_success_message,
                        submission_error_message,
                    });
                    commit('emailReceipts', {
                        send_admin_email,
                        admin_recipients,
                        include_admin_summary,
                        admin_email_message,
                        send_stakeholder_email,
                        stakeholder_reply_to,
                        include_stakeholder_summary,
                        stakeholder_email_message,
                        stakeholder_email_footer,
                        stakeholder_email_subject,
                        hide_stakeholder_email_header,
                        hide_admin_email_header,
                    });
                    commit('issues', categories);

                    return newForm;
                },
            },
        },
        /**
         * forms/edit
         */
        edit: {
            namespaced: true,
            state: {
                ...defaultState(),
            },
            getters,
            mutations,
            actions: {
                ...actions,
                async fetch({ state, commit }, formId) {
                    const response = await Form.$find(formId);
                    const {
                        id,
                        name,
                        config,
                        submission_success_message,
                        submission_error_message,
                        send_admin_email,
                        admin_recipients,
                        include_admin_summary,
                        admin_email_message,
                        send_stakeholder_email,
                        stakeholder_reply_to,
                        include_stakeholder_summary,
                        stakeholder_email_message,
                        stakeholder_email_footer,
                        stakeholder_email_subject,
                        hide_stakeholder_email_header,
                        hide_admin_email_header,
                        categories,
                    } = response;

                    // merge missing fields (legacy forms)
                    config.meta = {...state.form.meta, ...config.meta };
                    commit('id', id);
                    commit('name', name);
                    commit('form', config);
                    commit('messages', {
                        submission_success_message,
                        submission_error_message,
                    });
                    commit('emailReceipts', {
                        send_admin_email,
                        admin_recipients,
                        include_admin_summary,
                        admin_email_message,
                        send_stakeholder_email,
                        stakeholder_reply_to,
                        include_stakeholder_summary,
                        stakeholder_email_message,
                        stakeholder_email_footer,
                        stakeholder_email_subject,
                        hide_stakeholder_email_header,
                        hide_admin_email_header,
                    });
                    commit('issues', {
                        categories: {
                            sync: categories.data.map(category => category.id)
                        }
                    });
                },
                async saveForm({ state }) {
                    const form = await Form.$find(state.id);
                    const payload = {
                        name: state.name,
                        config: state.form,
                        ...state.messages,
                        ...state.emailReceipts,
                        ...state.issues,
                    };
                    return await form.patch(payload);
                },
                async deleteForm({ state }) {
                    const form = await Form.$find(state.id);
                    const deleteForm = await form.delete();
                    state = {...defaultState()};
                    return deleteForm;
                },
            },
        },
    },
};
